// MetricCard — tarjeta KPI compartida (padding 18px, radius 12,
// colorBorderSecondary + colorBgContainer + boxShadowTertiary, icon+label,
// Statistic). Unifica las 3 copias locales de la familia de KPIs del cliente
// 360 (ClienteKPIs, ClienteKPIsFinancieros, ClienteCicloFacturacionCard).
import type { CSSProperties, ReactNode } from 'react';
import { Spin, Statistic, Typography, theme } from 'antd';

const { Text } = Typography;

const VALUE_FONT_SIZES = {
  lg: 26,
  md: 22,
} as const;

export interface MetricCardProps {
  label: string;
  loading?: boolean;
  icon?: ReactNode;
  /** Valor principal; string o ReactNode (Statistic con valueRender). */
  value?: ReactNode;
  /** Tamaño del valor: 'lg' (26px) | 'md' (22px). Default 'lg'. */
  valueSize?: keyof typeof VALUE_FONT_SIZES;
  /** Color del valor (accent). */
  color?: string;
  caption?: ReactNode;
  /** Reemplaza Statistic + caption por contenido custom (p. ej. ciclo). */
  children?: ReactNode;
  style?: CSSProperties;
}

export function MetricCard({
  label,
  loading = false,
  icon,
  value,
  valueSize = 'lg',
  color,
  caption,
  children,
  style,
}: MetricCardProps) {
  const { token } = theme.useToken();
  return (
    <div
      style={{
        height: '100%',
        padding: '18px 18px 20px',
        borderRadius: 12,
        border: `1px solid ${token.colorBorderSecondary}`,
        background: token.colorBgContainer,
        boxShadow: token.boxShadowTertiary,
        ...style,
      }}
    >
      <Text
        type="secondary"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 13,
          fontWeight: 500,
          marginBottom: 12,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
        }}
      >
        {icon && (
          <span
            aria-hidden
            style={{ display: 'inline-flex', fontSize: 18, lineHeight: 1, flexShrink: 0 }}
          >
            {icon}
          </span>
        )}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }} title={label}>
          {label}
        </span>
      </Text>
      {loading ? (
        <div style={{ padding: '8px 0' }}>
          <Spin size="small" />
        </div>
      ) : children !== undefined ? (
        children
      ) : (
        <>
          {value !== undefined && (
            <Statistic
              valueRender={() => value}
              styles={{
                content: {
                  fontSize: VALUE_FONT_SIZES[valueSize],
                  fontWeight: 600,
                  letterSpacing: '-0.015em',
                  lineHeight: 1.2,
                  color,
                },
              }}
            />
          )}
          {caption !== undefined && (
            <Text
              type="secondary"
              title={typeof caption === 'string' ? caption : undefined}
              style={{
                display: 'block',
                maxWidth: '100%',
                marginTop: 6,
                fontSize: 12,
                lineHeight: 1.4,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {caption}
            </Text>
          )}
        </>
      )}
    </div>
  );
}
